import { useState, useCallback, useEffect } from "react"
import { Loader2, Sparkles, X, Check, Plus, RefreshCw } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { ThemeEvidenceBadge } from "@/components/strategy/ThemeEvidenceBadge"
import { cn } from "@/lib/utils"
import { useStrategyStore, type PendingThemeSuggestion } from "@/stores/strategy-store"

interface AddThemeWithAiDialogProps {
  bidId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (candidate: PendingThemeSuggestion) => Promise<void>
}

const CATEGORY_LABELS: Record<string, string> = {
  experience: 'Experience',
  personnel: 'Personnel',
  methodology: 'Methodology',
  innovation: 'Innovation',
  relationship: 'Relationship',
  value: 'Value',
  risk: 'Risk',
  local_presence: 'Local presence',
  other: 'Other',
}

export function AddThemeWithAiDialog({ bidId, open, onOpenChange, onConfirm }: AddThemeWithAiDialogProps) {
  const addThemeWithAi = useStrategyStore(s => s.addThemeWithAi)

  const [prompt, setPrompt] = useState('')
  const [candidates, setCandidates] = useState<PendingThemeSuggestion[] | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [adding, setAdding] = useState(false)

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setPrompt('')
      setCandidates(null)
      setSelectedId(null)
      setGenerating(false)
      setAdding(false)
    }
  }, [open])

  const handleGenerate = useCallback(async () => {
    const trimmed = prompt.trim()
    if (!trimmed || generating) return
    setGenerating(true)
    try {
      const result = await addThemeWithAi(bidId, trimmed)
      setCandidates(result)
      setSelectedId(result[0]?.id ?? null)
    } finally {
      setGenerating(false)
    }
  }, [prompt, generating, addThemeWithAi, bidId])

  const handleAdd = useCallback(async () => {
    if (!candidates || !selectedId) return
    const candidate = candidates.find(c => c.id === selectedId)
    if (!candidate) return
    setAdding(true)
    try {
      await onConfirm(candidate)
      onOpenChange(false)
    } finally {
      setAdding(false)
    }
  }, [candidates, selectedId, onConfirm, onOpenChange])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && prompt.trim() && !generating) {
      e.preventDefault()
      void handleGenerate()
    }
  }, [prompt, generating, handleGenerate])

  // Editing the prompt after candidates appear clears selection so user re-picks after regen
  const handlePromptChange = useCallback((value: string) => {
    setPrompt(value)
    if (candidates) {
      setSelectedId(null)
    }
  }, [candidates])

  const hasCandidates = candidates !== null && candidates.length > 0
  const promptDisabled = generating
  const primaryDisabled = generating || adding || !prompt.trim() || (hasCandidates && !selectedId)
  const primaryLabel = !hasCandidates ? (generating ? 'Generating…' : 'Generate candidates') : 'Add to bid'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 flex-row items-start gap-3 space-y-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#8A6BD1] to-[hsl(var(--ai-accent))]">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <DialogTitle className="text-sm font-medium">Add a theme with AI</DialogTitle>
            <DialogDescription className="text-xs mt-0.5">
              {!hasCandidates
                ? 'Describe a theme or focus area. AI proposes 2–3 options grounded in this tender.'
                : 'Pick one candidate to add to this bid.'}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="px-5 pb-3">
          <div className="relative">
            <Textarea
              value={prompt}
              onChange={e => handlePromptChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. We have a strong record on whole-of-life cost modelling — I want a theme around that."
              rows={3}
              disabled={promptDisabled}
              className="text-sm resize-none focus-visible:ring-[hsl(var(--ai-accent))]"
            />
            {hasCandidates && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute bottom-1.5 right-1.5 h-6 px-2 text-[11px]"
                onClick={() => void handleGenerate()}
                disabled={generating || !prompt.trim()}
              >
                <RefreshCw className={cn("h-2.5 w-2.5 mr-1", generating && "animate-spin")} />
                Regenerate
              </Button>
            )}
          </div>
        </div>

        <div className="px-5 pb-4 min-h-[120px]">
          {generating && !hasCandidates && (
            <div className="space-y-2">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2 mb-2">
                <Loader2 className="h-3 w-3 animate-spin text-[hsl(var(--ai-accent))]" />
                Generating candidates…
              </div>
              <div className="rounded-lg p-3 bg-muted space-y-2">
                <div className="h-3 rounded bg-border w-3/5" />
                <div className="h-2 rounded bg-border w-11/12" />
                <div className="h-2 rounded bg-border w-2/3" />
              </div>
              <div className="rounded-lg p-3 bg-muted space-y-2">
                <div className="h-3 rounded bg-border w-1/2" />
                <div className="h-2 rounded bg-border w-10/12" />
              </div>
            </div>
          )}

          {!generating && !hasCandidates && (
            <div className="rounded-lg p-6 text-center bg-muted/60 border border-dashed border-border">
              <Sparkles className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              <div className="text-meta">
                Candidates will appear here once you generate.
              </div>
            </div>
          )}

          {hasCandidates && (
            <>
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
                {candidates.length} candidate{candidates.length === 1 ? '' : 's'} · click to select
              </div>
              <div className="space-y-2">
                {candidates.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={cn(
                      "w-full text-left rounded-lg border p-3 transition-all",
                      selectedId === c.id
                        ? "border-[hsl(var(--ai-accent))] ring-2 ring-[hsl(var(--ai-accent)/0.12)] bg-[hsl(var(--ai-accent-soft)/0.3)]"
                        : "border-border bg-card hover:border-[hsl(var(--ai-accent))]"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{c.title}</span>
                        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] bg-[hsl(var(--ai-accent-soft))] text-[hsl(var(--ai-accent-text))]">
                          <Sparkles className="h-2.5 w-2.5" />
                          New
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <ThemeEvidenceBadge strength={c.evidenceStrength} count={c.libraryEvidenceCount} />
                        {selectedId === c.id && <Check className="h-3.5 w-3.5 text-[hsl(var(--ai-accent))]" />}
                      </div>
                    </div>
                    {c.description && (
                      <p className="text-meta leading-relaxed mb-1">{c.description}</p>
                    )}
                    <div className="text-[11px] text-muted-foreground">
                      {CATEGORY_LABELS[c.category] ?? c.category}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 bg-muted border-t border-border">
          <span className="text-meta">
            {hasCandidates ? (selectedId ? `1 of ${candidates.length} selected` : 'Pick one to add') : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              {hasCandidates ? <><X className="h-3.5 w-3.5 mr-1" />Cancel</> : 'Cancel'}
            </Button>
            <Button
              size="sm"
              disabled={primaryDisabled}
              onClick={() => (hasCandidates ? void handleAdd() : void handleGenerate())}
            >
              {generating ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : !hasCandidates ? (
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              ) : (
                <Plus className="h-3.5 w-3.5 mr-1.5" />
              )}
              {primaryLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
